// ============================================================
// messages.ts — Message encoding/decoding for client-server comms
// Supports JSON (fallback) and FlatBuffer binary encoding.
// Includes delta decompression for bandwidth efficiency.
// ============================================================

import * as flatbuffers from 'flatbuffers';
import { MAX_PLAYERS } from '../core/config';

// N-M-007: wire-level cap on the per-tick player count. MAX_PLAYERS is the
// gameplay-level match roster (10 for 5v5), but the wire allows a generous
// over-cap to absorb spectators / future game modes without truncating the
// delta. Anything beyond this is treated as a malformed payload — the prior
// `playerCount < 0` branch was dead because the source is a uint16 reader.
const BINARY_DELTA_MAX_PLAYERS = Math.max(MAX_PLAYERS, 32);

// ============================================================
// Interfaces
// ============================================================

export interface InputMessage {
  sequence: number;
  /** Bitfield: bit0=W bit1=A bit2=S bit3=D bit4=Jump bit5=Crouch bit6=Walk */
  keys: number;
  yaw: number;
  pitch: number;
  /** Bitfield: bit0=Fire bit1=Reload bit2=Use bit3=Drop bit4=Inspect */
  actions: number;
  weaponSlot: number;
  /** Frame delta seconds (N-C-003). Encoded into slot 6 of the FlatBuffer
   *  ClientInput; server decodes it into ClientInput.DeltaTime. Optional
   *  for tests / synthetic inputs — when omitted the encoder skips slot 6
   *  and the server falls back to its default tick duration. */
  dt?: number;
}

export interface PlayerSnapshot {
  id: number;
  position: { x: number; y: number; z: number };
  yaw: number;
  pitch: number;
  health: number;
  armor: number;
  weapon: string;
  isCrouching: boolean;
  isGrounded?: boolean;
  isSliding?: boolean;
  /** Character skin / player model skin ID. */
  skinId?: number;
  /** Currently equipped weapon skin ID. */
  weaponSkinId?: number;
  money?: number;
  team?: number;
  grenades?: Record<string, number>;
  /** M6: Whether this player is currently reloading */
  reloading?: boolean;
  /** H2: Whether this player is inspecting their weapon */
  inspecting?: boolean;
  /** G6: True when this player is the assigned C4 carrier this round. */
  isBombCarrier?: boolean;
  /** Protocol v7 — true while attached to a ladder volume. */
  isOnLadder?: boolean;
  /** Protocol v7 — 4-bit ladder id (0..15) of the active ladder. */
  ladderId?: number;
  /** Protocol v7 — true while the procedural Bezier mantle driver is in flight. */
  isMantling?: boolean;
  /** Protocol v7 — destination feet position for the active mantle (12 B). */
  mantleDest?: { x: number; y: number; z: number };
  /** Protocol v7 — quantised mantle progress (0..255 over 0.45 s). */
  mantleT?: number;
  /** Protocol v7 — true while the body-centre probe is inside a water volume. */
  isInWater?: boolean;
  /**
   * GP-W3: Server-authoritative set of weapon IDs the player currently owns.
   * Decoded from the JSON-encoded `ownedWeapons` field in the snapshot
   * (parallel to `grenades`). Absent on snapshots that pre-date the wire
   * upgrade — callers must treat `undefined` as "unknown" and skip the
   * already-owned check.
   */
  ownedWeapons?: Record<string, boolean>;
}

/**
 * Protocol v7 — replicated door state for the visual angle interpolator.
 * The client computes the displayed swing angle as
 *   `(now - stateStart) / animTime`, clamped to [0,1], then mapped to
 * [0, swingAngleDeg]. Collision uses the discrete `state` value — the
 * server is authoritative on whether the door is open / closed.
 */
export interface DoorStateSnapshot {
  id: string;
  /** 0=Closed 1=Opening 2=Open 3=Closing */
  state: number;
  stateStart: number;
  animTime: number;
}

export interface WorldSnapshot {
  tick: number;
  yourSequenceAck: number;
  players: PlayerSnapshot[];
  events: GameEvent[];
}

/** Extended snapshot with metadata fields from delta encoding. */
export interface ExtendedSnapshot extends WorldSnapshot {
  timeRemaining?: number;
  roundPhase?: string;
  roundNumber?: number;
  teamScores?: number[];
  bomb?: BinaryBombState | null;
  /** Protocol v7 — door state machine snapshots for visual angle lerping. */
  doorStates?: DoorStateSnapshot[];
}

/** Bomb state decoded from binary delta snapshots. */
export interface BinaryBombState {
  isPlanted: boolean;
  isDetonated: boolean;
  isDefused: boolean;
  isPlanting: boolean;
  isDefusing: boolean;
  planterId: number;
  defuserId: number;
  timer: number;
  plantProgress: number;
  defuseProgress: number;
  plantSite: string;
}

export interface GameEvent {
  type:
    | 'kill' | 'damage' | 'respawn'
    | 'weapon_fire'
    | 'grenade_throw' | 'grenade_detonate' | 'flash_blind'
    | 'fire_damage'
    | 'roundEnd' | 'round_start' | 'match_end'
    | 'team_change'
    | 'team_full'
    | 'bomb_planted' | 'bomb_defused' | 'bomb_exploded';
  data: Record<string, any>;
}

// --- Key bitfield helpers ---

export const Keys = {
  W: 1 << 0,
  A: 1 << 1,
  S: 1 << 2,
  D: 1 << 3,
  Jump: 1 << 4,
  Crouch: 1 << 5,
  Walk: 1 << 6,
} as const;

export const Actions = {
  Fire: 1 << 0,
  Reload: 1 << 1,
  Use: 1 << 2,
  Drop: 1 << 3,
  Inspect: 1 << 4,
} as const;

// ============================================================
// Delta snapshot fields bitfield
// ============================================================

/** Bitfield flags indicating which fields are present in a delta update. */
export const DeltaFields = {
  PosX: 1 << 0,
  PosY: 1 << 1,
  PosZ: 1 << 2,
  Yaw: 1 << 3,
  Pitch: 1 << 4,
  Health: 1 << 5,
  Armor: 1 << 6,
  Weapon: 1 << 7,
  IsCrouching: 1 << 8,
  SkinId: 1 << 9,
  IsGrounded: 1 << 10,
  IsSliding: 1 << 11,
  Removed: 1 << 12,
  WeaponSkinId: 1 << 13,
  Reloading: 1 << 14,
  Inspecting: 1 << 15,
  BombCarrier: 1 << 16,
  IsOnLadder: 1 << 17,
  LadderId: 1 << 18,
  IsMantling: 1 << 19,
  IsInWater: 1 << 20,
  MantleDest: 1 << 21,
  MantleT: 1 << 22,
  // F-NC-007 / F-NC-008: client-side fieldMask bits for Money and Grenades so
  // applyDelta can distinguish "explicitly sent" from "no update."
  Money: 1 << 23,
  Grenades: 1 << 24,
  // F-NC-006: team. (SkinId is reused for both player-skin keyframes and the
  // new F-NC-005 delta path — applyDelta merges either source into prev.skinId.)
  Team: 1 << 25,
  // N-M-002: ownedWeapons fieldMask bit so applyDelta + binary decode can
  // distinguish an explicit owned-weapons update from "no update this tick"
  // and N-M-005 can wire delta-encoded ownership into the buy-menu rebuy gate.
  OwnedWeapons: 1 << 26,
} as const;

export interface DeltaPlayerSnapshot {
  id: number;
  /** Bitfield indicating which fields are present. */
  fieldMask: number;
  posX?: number;
  posY?: number;
  posZ?: number;
  yaw?: number;
  pitch?: number;
  health?: number;
  armor?: number;
  weapon?: string;
  isCrouching?: boolean;
  skinId?: number;
  weaponSkinId?: number;
  money?: number;
  team?: number;
  grenades?: Record<string, number>;
  isGrounded?: boolean;
  isSliding?: boolean;
  reloading?: boolean;
  inspecting?: boolean;
  isBombCarrier?: boolean;
  isOnLadder?: boolean;
  ladderId?: number;
  isMantling?: boolean;
  isInWater?: boolean;
  mantleDest?: { x: number; y: number; z: number };
  mantleT?: number;
  ownedWeapons?: Record<string, boolean>;
  removed?: boolean;
}

export interface DeltaWorldSnapshot {
  tick: number;
  yourSequenceAck: number;
  players: DeltaPlayerSnapshot[];
  events: GameEvent[];
  isDelta: true;
}

// ============================================================
// Delta Decoder — stores last known full state per player
// ============================================================

export class DeltaDecoder {
  private lastKnown = new Map<number, PlayerSnapshot>();

  /**
   * Apply a delta snapshot, merging changed fields into last known state.
   * On first snapshot for a player (no prior state), treat delta as full.
   */
  applyDelta(delta: DeltaPlayerSnapshot): PlayerSnapshot {
    const prev = this.lastKnown.get(delta.id);

    const full: PlayerSnapshot = {
      id: delta.id,
      position: {
        x: (delta.fieldMask & DeltaFields.PosX) ? delta.posX! : (prev?.position.x ?? 0),
        y: (delta.fieldMask & DeltaFields.PosY) ? delta.posY! : (prev?.position.y ?? 0),
        z: (delta.fieldMask & DeltaFields.PosZ) ? delta.posZ! : (prev?.position.z ?? 0),
      },
      yaw: (delta.fieldMask & DeltaFields.Yaw) ? delta.yaw! : (prev?.yaw ?? 0),
      pitch: (delta.fieldMask & DeltaFields.Pitch) ? delta.pitch! : (prev?.pitch ?? 0),
      health: (delta.fieldMask & DeltaFields.Health) ? delta.health! : (prev?.health ?? 100),
      armor: (delta.fieldMask & DeltaFields.Armor) ? delta.armor! : (prev?.armor ?? 0),
      weapon: (delta.fieldMask & DeltaFields.Weapon) ? delta.weapon! : (prev?.weapon ?? 'glock'),
      isCrouching: (delta.fieldMask & DeltaFields.IsCrouching) ? delta.isCrouching! : (prev?.isCrouching ?? false),
      isGrounded: (delta.fieldMask & DeltaFields.IsGrounded) ? delta.isGrounded! : (prev?.isGrounded ?? true),
      isSliding: (delta.fieldMask & DeltaFields.IsSliding) ? delta.isSliding! : (prev?.isSliding ?? false),
      skinId: (delta.fieldMask & DeltaFields.SkinId) ? delta.skinId! : (prev?.skinId ?? 0),
      weaponSkinId: (delta.fieldMask & DeltaFields.WeaponSkinId) ? delta.weaponSkinId! : (prev?.weaponSkinId ?? 0),
      money: (delta.fieldMask & DeltaFields.Money) ? (delta.money ?? 0) : (prev?.money ?? 0),
      team: (delta.fieldMask & DeltaFields.Team) ? (delta.team ?? 0) : (prev?.team ?? 0),
      grenades: (delta.fieldMask & DeltaFields.Grenades) ? (delta.grenades ?? {}) : prev?.grenades,
      reloading: (delta.fieldMask & DeltaFields.Reloading) ? delta.reloading! : (prev?.reloading ?? false),
      inspecting: (delta.fieldMask & DeltaFields.Inspecting) ? delta.inspecting! : (prev?.inspecting ?? false),
      isBombCarrier: (delta.fieldMask & DeltaFields.BombCarrier) ? delta.isBombCarrier! : (prev?.isBombCarrier ?? false),
      isOnLadder: (delta.fieldMask & DeltaFields.IsOnLadder) ? delta.isOnLadder! : (prev?.isOnLadder ?? false),
      ladderId: (delta.fieldMask & DeltaFields.LadderId) ? delta.ladderId! : (prev?.ladderId ?? 0),
      isMantling: (delta.fieldMask & DeltaFields.IsMantling) ? delta.isMantling! : (prev?.isMantling ?? false),
      mantleDest: (delta.fieldMask & DeltaFields.MantleDest) ? delta.mantleDest : prev?.mantleDest,
      mantleT: (delta.fieldMask & DeltaFields.MantleT) ? delta.mantleT! : (prev?.mantleT ?? 0),
      isInWater: (delta.fieldMask & DeltaFields.IsInWater) ? delta.isInWater! : (prev?.isInWater ?? false),
      // N-M-005: prefer the masked fieldMask path so an explicit empty
      // ownership set (e.g. round reset) overrides stale prior state. Fall
      // back to the raw field for legacy callers that bypass the mask.
      ownedWeapons: (delta.fieldMask & DeltaFields.OwnedWeapons)
        ? (delta.ownedWeapons ?? {})
        : (delta.ownedWeapons ?? prev?.ownedWeapons),
    };

    this.lastKnown.set(delta.id, full);
    return full;
  }

  /** Apply a full (non-delta) player snapshot — updates stored state. */
  applyFull(player: PlayerSnapshot): PlayerSnapshot {
    this.lastKnown.set(player.id, { ...player });
    return player;
  }

  /** Apply a full world snapshot and update all stored states. */
  applyFullWorld(snapshot: WorldSnapshot): WorldSnapshot {
    for (const p of snapshot.players) {
      this.lastKnown.set(p.id, { ...p });
    }
    return snapshot;
  }

  /** Apply a delta world snapshot, producing a full WorldSnapshot. */
  applyDeltaWorld(delta: DeltaWorldSnapshot): WorldSnapshot {
    const changed = new Set<number>();
    const players: PlayerSnapshot[] = [];
    for (const dp of delta.players) {
      if (dp.removed) {
        this.removePlayer(dp.id);
        changed.add(dp.id);
        continue;
      }
      players.push(this.applyDelta(dp));
      changed.add(dp.id);
    }
    // Include unchanged players from last known so downstream consumers see
    // a full roster every tick instead of only the changed subset.
    for (const [id, snap] of this.lastKnown) {
      if (!changed.has(id)) {
        players.push({ ...snap });
      }
    }
    return {
      tick: delta.tick,
      yourSequenceAck: delta.yourSequenceAck,
      players,
      events: delta.events,
    };
  }

  /** Reset all stored state (e.g. on roundStart). */
  reset(): void {
    this.lastKnown.clear();
  }

  /** Remove a specific player's stored state. */
  removePlayer(id: number): void {
    this.lastKnown.delete(id);
  }
}

// ============================================================
// FlatBuffer Layout Constants
// ============================================================

// FlatBuffer table field offsets for our schema.
// These match the server's FlatBuffer schema layout.

// ClientInput table (BUG-PROTO-4: tick removed):
//   sequence(int32), keys(uint8), yaw(float32), pitch(float32),
//   actions(uint8), weaponSlot(uint8), dt(float32)

// PlayerSnapshotFB table: id(uint16), posX(float32), posY(float32), posZ(float32),
//                          yaw(float32), pitch(float32), health(uint8), armor(uint8),
//                          weapon(string), isCrouching(bool), skinId(uint16),
//                          money(uint16), team(uint8), isGrounded(bool),
//                          isSliding(bool), grenades(string), weaponSkinId(uint16)

// WorldSnapshotFB table: tick(uint32), yourSequenceAck(uint32),
//                         players([PlayerSnapshotFB]), events([GameEventFB])

// GameEventFB table: type(string), data(string) — data is JSON-encoded

// ============================================================
// FlatBuffer Encoding — ClientInput
// ============================================================

/**
 * Encode a ClientInput as a FlatBuffer binary message.
 * Layout: non-size-prefixed FlatBuffer.
 *
 * BUG-PROTO-4: tick was removed from the schema — the server decoder
 * never used it and dropping the field saves ~16 KB/s @ 64 Hz × 5 players.
 * Slots renumber: keys 2→1, yaw 3→2, pitch 4→3, actions 5→4, weaponSlot 6→5.
 *
 * N-C-003: dt (frame delta seconds) is encoded at slot 6 — server reads it
 * into ClientInput.DeltaTime (see server/internal/net/protocol.go:439-442).
 * When input.dt is omitted, slot 6 is skipped and the server falls back
 * to its default; the default-zero path is exercised by the encoder test.
 *
 * N-L-005: Reserved for the binary-input upgrade (N-C-002 future work).
 * Production input on the hot path uses the slot-keyed Uint8Array encoder
 * in `connection.ts`; this FlatBuffer helper is currently exercised only
 * by `messages.test.ts` round-trip tests and is kept as the reference
 * encoder against which the hand-rolled wire layout is validated.
 */
export function encodeInputBinary(input: InputMessage): ArrayBuffer {
  const builder = new flatbuffers.Builder(128);

  // Build ClientInput table — 7 fields (slots 0..6). Slot 6 is dt; the
  // FlatBuffer default of 0 means the field is omitted when input.dt is
  // undefined, so existing 6-slot wire formats stay forward-compatible.
  builder.startObject(7);

  // Field 0: sequence (int32) — voffset slot 4
  builder.addFieldInt32(0, input.sequence, 0);
  // Field 1: keys (uint8) — voffset slot 6
  builder.addFieldInt8(1, input.keys, 0);
  // Field 2: yaw (float32) — voffset slot 8
  builder.addFieldFloat32(2, input.yaw, 0);
  // Field 3: pitch (float32) — voffset slot 10
  builder.addFieldFloat32(3, input.pitch, 0);
  // Field 4: actions (uint8) — voffset slot 12
  builder.addFieldInt8(4, input.actions, 0);
  // Field 5: weaponSlot (uint8) — voffset slot 14
  builder.addFieldInt8(5, input.weaponSlot, 0);
  // Field 6: dt (float32) — voffset slot 16. N-C-003: server reads this
  // into ClientInput.DeltaTime. Default 0 = omit field on wire.
  builder.addFieldFloat32(6, input.dt ?? 0, 0);

  const inputOffset = builder.endObject();
  builder.finish(inputOffset);

  const bytes = builder.asUint8Array();
  const copy = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(copy).set(bytes);
  return copy;
}

// ============================================================
// FlatBuffer Decoding — WorldSnapshot
// ============================================================

/**
 * Decode a FlatBuffer WorldSnapshot from binary data.
 * Expects a non-size-prefixed FlatBuffer (standard Finish).
 */
function decodeFlatBufferSnapshot(data: ArrayBuffer): ExtendedSnapshot | null {
  try {
  const bytes = new Uint8Array(data);
  const buf = new flatbuffers.ByteBuffer(bytes);

  // Root table offset is the first 4 bytes (non-size-prefixed FlatBuffer)
  const rootOffset = buf.readInt32(0);
  if (rootOffset < 0 || rootOffset >= buf.bytes().length) {
    return null;
  }

  // Helper to read table fields
  const readField = (tableOffset: number, fieldIndex: number): number | null => {
    const vtableOffset = tableOffset - buf.readInt32(tableOffset);
    const vtableSize = buf.readInt16(vtableOffset);
    const fieldSlot = 4 + fieldIndex * 2; // vtable: size(2) + tableSize(2) + fields...
    if (fieldSlot >= vtableSize) return null;
    const fieldRelOffset = buf.readInt16(vtableOffset + fieldSlot);
    if (fieldRelOffset === 0) return null;
    return tableOffset + fieldRelOffset;
  };

  // Read WorldSnapshot root table — Protocol v7 (9 slots).
  const tickPos = readField(rootOffset, 0);
  const ackPos = readField(rootOffset, 1);
  const playersVecPos = readField(rootOffset, 2);
  const eventsVecPos = readField(rootOffset, 3);
  const timeRemainingPos = readField(rootOffset, 4);
  const roundPhasePos = readField(rootOffset, 5);
  const roundNumberPos = readField(rootOffset, 6);
  const teamScoresPos = readField(rootOffset, 7);
  const doorStatesVecPos = readField(rootOffset, 8);

  const tick = tickPos !== null ? buf.readUint32(tickPos) : 0;
  const yourSequenceAck = ackPos !== null ? buf.readUint32(ackPos) : 0;

  // Read players vector
  const players: PlayerSnapshot[] = [];
  if (playersVecPos !== null) {
    const vecOffset = playersVecPos + buf.readInt32(playersVecPos);
    const vecLen = buf.readInt32(vecOffset);
    for (let i = 0; i < vecLen; i++) {
      const entryOffset = vecOffset + 4 + i * 4;
      const playerTableOffset = entryOffset + buf.readInt32(entryOffset);
      players.push(decodePlayerFromTable(buf, playerTableOffset));
    }
  }

  // Read events vector
  const events: GameEvent[] = [];
  if (eventsVecPos !== null) {
    const vecOffset = eventsVecPos + buf.readInt32(eventsVecPos);
    const vecLen = buf.readInt32(vecOffset);
    for (let i = 0; i < vecLen; i++) {
      const entryOffset = vecOffset + 4 + i * 4;
      const eventTableOffset = entryOffset + buf.readInt32(entryOffset);
      events.push(decodeEventFromTable(buf, eventTableOffset));
    }
  }

  // Read extended fields (present in server FlatBuffer but previously unused)
  let timeRemaining: number | undefined;
  if (timeRemainingPos !== null) {
    timeRemaining = buf.readFloat32(timeRemainingPos);
  }

  let roundPhase: string | undefined;
  if (roundPhasePos !== null) {
    const strOffset = roundPhasePos + buf.readInt32(roundPhasePos);
    const strLen = buf.readInt32(strOffset);
    const bufBytes = buf.bytes();
    if (strLen >= 0 && strOffset + 4 + strLen <= bufBytes.length) {
      roundPhase = new TextDecoder().decode(bufBytes.subarray(strOffset + 4, strOffset + 4 + strLen));
    } else {
      roundPhase = '';
    }
  }

  let roundNumber: number | undefined;
  if (roundNumberPos !== null) {
    roundNumber = buf.bytes()[roundNumberPos];
  }

  let teamScores: number[] | undefined;
  if (teamScoresPos !== null) {
    const vecOffset = teamScoresPos + buf.readInt32(teamScoresPos);
    const vecLen = buf.readInt32(vecOffset);
    const scoresBytes = buf.bytes();
    if (vecLen >= 3 && vecOffset + 4 + 3 <= scoresBytes.length) {
      teamScores = [
        scoresBytes[vecOffset + 4],
        scoresBytes[vecOffset + 5],
        scoresBytes[vecOffset + 6],
      ];
    }
  }

  // Read door_states vector (Protocol v7 slot 8).
  let doorStates: DoorStateSnapshot[] | undefined;
  if (doorStatesVecPos !== null) {
    const vecOffset = doorStatesVecPos + buf.readInt32(doorStatesVecPos);
    const vecLen = buf.readInt32(vecOffset);
    if (vecLen > 0) {
      doorStates = [];
      for (let i = 0; i < vecLen; i++) {
        const entryOffset = vecOffset + 4 + i * 4;
        const tableOffset = entryOffset + buf.readInt32(entryOffset);
        doorStates.push(decodeDoorStateFromTable(buf, tableOffset));
      }
    }
  }

  return { tick, yourSequenceAck, players, events, timeRemaining, roundPhase, roundNumber, teamScores, doorStates };
  } catch (e) {
    console.error('FlatBuffer decode error:', e);
    return null;
  }
}

function decodePlayerFromTable(buf: flatbuffers.ByteBuffer, tableOffset: number): PlayerSnapshot {
  const readField = (fieldIndex: number): number | null => {
    const vtableOffset = tableOffset - buf.readInt32(tableOffset);
    const vtableSize = buf.readInt16(vtableOffset);
    const fieldSlot = 4 + fieldIndex * 2;
    if (fieldSlot >= vtableSize) return null;
    const fieldRelOffset = buf.readInt16(vtableOffset + fieldSlot);
    if (fieldRelOffset === 0) return null;
    return tableOffset + fieldRelOffset;
  };

  const idPos = readField(0);
  const posXPos = readField(1);
  const posYPos = readField(2);
  const posZPos = readField(3);
  const yawPos = readField(4);
  const pitchPos = readField(5);
  const healthPos = readField(6);
  const armorPos = readField(7);
  const weaponPos = readField(8);
  const crouchPos = readField(9);
  const skinIdPos = readField(10);
  const moneyPos = readField(11);
  const teamPos = readField(12);
  const groundedPos = readField(13);
  const slidingPos = readField(14);
  const grenadesPos = readField(15);
  const weaponSkinIdPos = readField(16);
  const reloadingPos = readField(17);
  const inspectingPos = readField(18);
  const bombCarrierPos = readField(19);
  // Protocol v7 — Phase B3 ladder/mantle/water replication.
  const onLadderPos = readField(20);
  const ladderIdPos = readField(21);
  const mantlingPos = readField(22);
  const mantleDestXPos = readField(23);
  const mantleDestYPos = readField(24);
  const mantleDestZPos = readField(25);
  const mantleTPos = readField(26);
  const inWaterPos = readField(27);
  // GP-W3 — JSON-encoded ownedWeapons set (mirrors slot 15 grenades pattern).
  const ownedWeaponsPos = readField(28);

  let weapon = 'glock';
  if (weaponPos !== null) {
    const strOffset = weaponPos + buf.readInt32(weaponPos);
    const strLen = buf.readInt32(strOffset);
    // Bounds check: ensure string data is within buffer
    const bufBytes = buf.bytes();
    if (strOffset + 4 + strLen <= bufBytes.length && strLen >= 0) {
      const decoder = new TextDecoder();
      weapon = decoder.decode(bufBytes.subarray(strOffset + 4, strOffset + 4 + strLen));
    }
  }

  // Decode grenade inventory from JSON string (slot 15)
  let grenades: Record<string, number> | undefined;
  if (grenadesPos !== null) {
    try {
      const strOffset = grenadesPos + buf.readInt32(grenadesPos);
      const strLen = buf.readInt32(strOffset);
      const bufBytes = buf.bytes();
      if (strOffset + 4 + strLen <= bufBytes.length && strLen > 0) {
        const decoder = new TextDecoder();
        const jsonStr = decoder.decode(bufBytes.subarray(strOffset + 4, strOffset + 4 + strLen));
        grenades = JSON.parse(jsonStr);
      }
    } catch { /* ignore decode errors — grenades stay undefined */ }
  }

  // GP-W3: Decode ownedWeapons set from JSON string (slot 28). Server emits
  // the set as a JSON object `{glock: true, ak47: true, ...}`; absent on
  // older protocol revisions which is fine — buy menu treats undefined as
  // "unknown" and skips the rebuy gate.
  let ownedWeapons: Record<string, boolean> | undefined;
  if (ownedWeaponsPos !== null) {
    try {
      const strOffset = ownedWeaponsPos + buf.readInt32(ownedWeaponsPos);
      const strLen = buf.readInt32(strOffset);
      const bufBytes = buf.bytes();
      if (strOffset + 4 + strLen <= bufBytes.length && strLen > 0) {
        const decoder = new TextDecoder();
        const jsonStr = decoder.decode(bufBytes.subarray(strOffset + 4, strOffset + 4 + strLen));
        ownedWeapons = JSON.parse(jsonStr);
      }
    } catch { /* ignore decode errors — ownedWeapons stay undefined */ }
  }

  const mantleDest = (mantleDestXPos !== null || mantleDestYPos !== null || mantleDestZPos !== null)
    ? {
        x: mantleDestXPos !== null ? buf.readFloat32(mantleDestXPos) : 0,
        y: mantleDestYPos !== null ? buf.readFloat32(mantleDestYPos) : 0,
        z: mantleDestZPos !== null ? buf.readFloat32(mantleDestZPos) : 0,
      }
    : undefined;

  return {
    id: idPos !== null ? buf.readUint16(idPos) : 0,
    position: {
      x: posXPos !== null ? buf.readFloat32(posXPos) : 0,
      y: posYPos !== null ? buf.readFloat32(posYPos) : 0,
      z: posZPos !== null ? buf.readFloat32(posZPos) : 0,
    },
    yaw: yawPos !== null ? buf.readFloat32(yawPos) : 0,
    pitch: pitchPos !== null ? buf.readFloat32(pitchPos) : 0,
    health: healthPos !== null ? buf.bytes()[healthPos] : 100,
    armor: armorPos !== null ? buf.bytes()[armorPos] : 0,
    weapon,
    isCrouching: crouchPos !== null ? buf.bytes()[crouchPos] !== 0 : false,
    isGrounded: groundedPos !== null ? buf.bytes()[groundedPos] !== 0 : true,
    isSliding: slidingPos !== null ? buf.bytes()[slidingPos] !== 0 : false,
    skinId: skinIdPos !== null ? buf.readUint16(skinIdPos) : 0,
    weaponSkinId: weaponSkinIdPos !== null ? buf.readUint16(weaponSkinIdPos) : 0,
    money: moneyPos !== null ? buf.readUint16(moneyPos) : 0,
    team: teamPos !== null ? buf.bytes()[teamPos] : 0,
    grenades,
    reloading: reloadingPos !== null ? buf.bytes()[reloadingPos] !== 0 : false,
    inspecting: inspectingPos !== null ? buf.bytes()[inspectingPos] !== 0 : false,
    isBombCarrier: bombCarrierPos !== null ? buf.bytes()[bombCarrierPos] !== 0 : false,
    isOnLadder: onLadderPos !== null ? buf.bytes()[onLadderPos] !== 0 : false,
    ladderId: ladderIdPos !== null ? buf.bytes()[ladderIdPos] : 0,
    isMantling: mantlingPos !== null ? buf.bytes()[mantlingPos] !== 0 : false,
    mantleDest,
    mantleT: mantleTPos !== null ? buf.bytes()[mantleTPos] : 0,
    isInWater: inWaterPos !== null ? buf.bytes()[inWaterPos] !== 0 : false,
    ownedWeapons,
  };
}

/** Decode a single DoorStateMsg table (Protocol v7 WorldSnapshot slot 8). */
function decodeDoorStateFromTable(buf: flatbuffers.ByteBuffer, tableOffset: number): DoorStateSnapshot {
  const readField = (fieldIndex: number): number | null => {
    const vtableOffset = tableOffset - buf.readInt32(tableOffset);
    const vtableSize = buf.readInt16(vtableOffset);
    const fieldSlot = 4 + fieldIndex * 2;
    if (fieldSlot >= vtableSize) return null;
    const fieldRelOffset = buf.readInt16(vtableOffset + fieldSlot);
    if (fieldRelOffset === 0) return null;
    return tableOffset + fieldRelOffset;
  };

  const idPos = readField(0);
  const statePos = readField(1);
  const stateStartPos = readField(2);
  const animTimePos = readField(3);

  let id = '';
  if (idPos !== null) {
    const strOffset = idPos + buf.readInt32(idPos);
    const strLen = buf.readInt32(strOffset);
    const bufBytes = buf.bytes();
    if (strLen >= 0 && strOffset + 4 + strLen <= bufBytes.length) {
      id = new TextDecoder().decode(bufBytes.subarray(strOffset + 4, strOffset + 4 + strLen));
    }
  }

  return {
    id,
    state: statePos !== null ? buf.bytes()[statePos] : 0,
    stateStart: stateStartPos !== null ? buf.readFloat32(stateStartPos) : 0,
    animTime: animTimePos !== null ? buf.readFloat32(animTimePos) : 0,
  };
}

function decodeEventFromTable(buf: flatbuffers.ByteBuffer, tableOffset: number): GameEvent {
  const readField = (fieldIndex: number): number | null => {
    const vtableOffset = tableOffset - buf.readInt32(tableOffset);
    const vtableSize = buf.readInt16(vtableOffset);
    const fieldSlot = 4 + fieldIndex * 2;
    if (fieldSlot >= vtableSize) return null;
    const fieldRelOffset = buf.readInt16(vtableOffset + fieldSlot);
    if (fieldRelOffset === 0) return null;
    return tableOffset + fieldRelOffset;
  };

  const typePos = readField(0);
  const dataPos = readField(1);
  const decoder = new TextDecoder();

  let type: GameEvent['type'] = 'damage';
  if (typePos !== null) {
    const strOffset = typePos + buf.readInt32(typePos);
    const strLen = buf.readInt32(strOffset);
    const bufBytes = buf.bytes();
    if (strLen >= 0 && strOffset + 4 + strLen <= bufBytes.length) {
      type = decoder.decode(bufBytes.subarray(strOffset + 4, strOffset + 4 + strLen)) as GameEvent['type'];
    }
  }

  let data: Record<string, any> = {};
  if (dataPos !== null) {
    const strOffset = dataPos + buf.readInt32(dataPos);
    const strLen = buf.readInt32(strOffset);
    const bufBytes = buf.bytes();
    if (strLen >= 0 && strOffset + 4 + strLen <= bufBytes.length) {
      const jsonStr = decoder.decode(bufBytes.subarray(strOffset + 4, strOffset + 4 + strLen));
      try {
        data = JSON.parse(jsonStr);
      } catch {
        data = {};
      }
    }
  }

  return { type, data };
}

// ============================================================
// Binary Delta/Full Snapshot Decoding
// ============================================================
// Server wire format:
//   0xFE [FlatBuffer bytes]  — full keyframe snapshot
//   0xFD [binary delta]      — delta-compressed snapshot
//
// Delta binary layout after marker byte:
//   [4 bytes] tick (uint32 LE)
//   [4 bytes] sequence ack (uint32 LE)
//   [2 bytes] delta flags (uint16 LE)
//   [variable] field data based on flags

/** Server-side delta flags — must match server/internal/net/delta.go */
const ServerDeltaFlags = {
  Players:       1 << 0,
  Events:        1 << 1,
  TimeRemaining: 1 << 2,
  RoundPhase:    1 << 3,
  RoundNumber:   1 << 4,
  TeamScores:    1 << 5,
  Bomb:          1 << 6,
} as const;

/** Server-side player delta flags (uint32) — must match server/internal/net/delta.go */
const ServerPlayerDeltaFlags = {
  Position:    1 << 0,
  Yaw:         1 << 1,
  Pitch:       1 << 2,
  Health:      1 << 3,
  Armor:       1 << 4,
  Weapon:      1 << 5,
  Crouch:      1 << 6,
  Money:       1 << 7,
  Grounded:    1 << 8,
  Sliding:     1 << 9,
  Removed:     1 << 10,
  WeaponSkin:  1 << 11,
  Grenades:    1 << 12,
  Reloading:   1 << 13,
  Inspecting:  1 << 14,
  BombCarrier: 1 << 15,
  OnLadder:    1 << 16,
  Mantling:    1 << 17,
  InWater:     1 << 18,
  MantleDest:  1 << 19,
  MantleT:     1 << 20,
  LadderID:    1 << 21,
  // F-NC-005: player character SkinID delta flag (mirrors PlayerDeltaSkin server-side).
  Skin:        1 << 22,
  // F-NC-006: team byte delta flag (mirrors PlayerDeltaTeam server-side).
  Team:        1 << 23,
  // N-M-002: owned-weapons set (JSON-encoded Record<string,boolean>) — mirrors
  // PlayerDeltaOwnedWeapons server-side. The delta payload is a uint16 length
  // prefix + JSON bytes (same shape as Grenades / weapon_skin slot 28).
  OwnedWeapons: 1 << 24,
} as const;

/**
 * Check if an ArrayBuffer is a server binary snapshot (0xFD delta or 0xFE keyframe).
 */
export function isBinarySnapshot(data: ArrayBuffer): boolean {
  if (data.byteLength < 1) return false;
  const marker = new Uint8Array(data, 0, 1)[0];
  return marker === 0xFD || marker === 0xFE;
}

/**
 * Decode a binary snapshot from the server. Handles both delta (0xFD) and
 * full keyframe (0xFE) formats.
 *
 * Returns an object with `isDelta` flag and the decoded data. For delta
 * snapshots, only changed fields are present per player; the caller must
 * merge with the last-known full state using DeltaDecoder.
 */
export function decodeBinarySnapshot(
  data: ArrayBuffer,
  deltaDecoder: DeltaDecoder,
  lastState: ExtendedSnapshot | null,
): ExtendedSnapshot {
  const view = new DataView(data);
  const marker = view.getUint8(0);

  if (marker === 0xFE) {
    // Full keyframe — strip marker byte and decode as FlatBuffer
    const fbData = data.slice(1);
    const snap = decodeFlatBufferSnapshot(fbData);
    if (!snap) return { tick: 0, yourSequenceAck: 0, players: [], events: [] };
    deltaDecoder.applyFullWorld(snap);
    return snap;
  }

  if (marker !== 0xFD) {
    throw new Error(`Unknown binary snapshot marker: 0x${marker.toString(16)}`);
  }

  // Delta snapshot
  return decodeBinaryDelta(data, deltaDecoder, lastState);
}

/**
 * Decode a binary delta snapshot (0xFD marker).
 * Reads changed fields and merges them via DeltaDecoder.
 */
function decodeBinaryDelta(
  data: ArrayBuffer,
  deltaDecoder: DeltaDecoder,
  lastState: ExtendedSnapshot | null,
): ExtendedSnapshot {
  const view = new DataView(data);
  const bytes = new Uint8Array(data);
  let offset = 1; // skip marker byte

  // Tick
  const tick = view.getUint32(offset, true);
  offset += 4;

  // Sequence ack
  const yourSequenceAck = view.getUint32(offset, true);
  offset += 4;

  // Delta flags
  const flags = view.getUint16(offset, true);
  offset += 2;

  const players: PlayerSnapshot[] = [];
  const events: GameEvent[] = [];
  let timeRemaining = lastState?.timeRemaining;
  let roundPhase = lastState?.roundPhase;
  let roundNumber = lastState?.roundNumber;
  let teamScores = lastState?.teamScores;
  let bomb = lastState?.bomb;

  // --- Players ---
  if (flags & ServerDeltaFlags.Players) {
    const playerCount = view.getUint16(offset, true);
    offset += 2;
    // N-M-007: getUint16 already returns 0..65535, so the prior `< 0` branch
    // was unreachable. Cap against BINARY_DELTA_MAX_PLAYERS (= MAX_PLAYERS or
    // 32, whichever is larger) so a malformed/inflated count cannot drive an
    // unbounded loop.
    if (playerCount > BINARY_DELTA_MAX_PLAYERS) {
      return lastState ?? { tick, yourSequenceAck, players: [], events, timeRemaining, roundPhase, roundNumber, teamScores, bomb };
    }
    for (let i = 0; i < playerCount; i++) {
      const playerId = view.getUint16(offset, true); offset += 2;
      const pflags = view.getUint32(offset, true); offset += 4;
      const delta = decodeBinaryPlayerDelta(view, bytes, playerId, pflags, offset);
      offset = delta.newOffset;
      players.push(deltaDecoder.applyDelta(delta.player));
    }
  }

  // Include unchanged players from last state
  if (lastState) {
    const changedIds = new Set(players.map(p => p.id));
    for (const p of lastState.players) {
      if (!changedIds.has(p.id)) {
        players.push(p);
      }
    }
  }

  // --- Events ---
  if (flags & ServerDeltaFlags.Events) {
    const eventCount = view.getUint16(offset, true);
    offset += 2;
    for (let i = 0; i < eventCount; i++) {
      const typeLen = bytes[offset++];
      if (offset + typeLen > bytes.length) return lastState ?? { tick, yourSequenceAck, players, events, timeRemaining, roundPhase, roundNumber, teamScores, bomb };
      const typeStr = new TextDecoder().decode(bytes.subarray(offset, offset + typeLen));
      offset += typeLen;
      const dataLen = view.getUint16(offset, true);
      offset += 2;
      if (offset + dataLen > bytes.length) return lastState ?? { tick, yourSequenceAck, players, events, timeRemaining, roundPhase, roundNumber, teamScores, bomb };
      const dataJson = new TextDecoder().decode(bytes.subarray(offset, offset + dataLen));
      offset += dataLen;
      let eventData: Record<string, any> = {};
      try { eventData = JSON.parse(dataJson); } catch { /* empty */ }
      events.push({ type: typeStr as GameEvent['type'], data: eventData });
    }
  }

  // --- TimeRemaining ---
  if (flags & ServerDeltaFlags.TimeRemaining) {
    timeRemaining = view.getFloat32(offset, true);
    offset += 4;
  }

  // --- RoundPhase ---
  if (flags & ServerDeltaFlags.RoundPhase) {
    const phaseLen = bytes[offset++];
    if (offset + phaseLen > bytes.length) return lastState ?? { tick, yourSequenceAck, players, events, timeRemaining, roundPhase, roundNumber, teamScores, bomb };
    roundPhase = new TextDecoder().decode(bytes.subarray(offset, offset + phaseLen));
    offset += phaseLen;
  }

  // --- RoundNumber ---
  if (flags & ServerDeltaFlags.RoundNumber) {
    roundNumber = bytes[offset++];
  }

  // --- TeamScores ---
  if (flags & ServerDeltaFlags.TeamScores) {
    if (offset + 3 > bytes.length) return lastState ?? { tick, yourSequenceAck, players, events, timeRemaining, roundPhase, roundNumber, teamScores, bomb };
    teamScores = [bytes[offset], bytes[offset + 1], bytes[offset + 2]];
    offset += 3;
  }

  // --- Bomb ---
  if (flags & ServerDeltaFlags.Bomb) {
    const bombResult = decodeBinaryBomb(view, bytes, offset);
    bomb = bombResult.bomb;
    offset = bombResult.newOffset;
  }

  return {
    tick,
    yourSequenceAck,
    players,
    events,
    timeRemaining,
    roundPhase,
    roundNumber,
    teamScores,
    bomb,
  };
}

/**
 * Decode a single player's delta fields from binary.
 */
function decodeBinaryPlayerDelta(
  view: DataView,
  bytes: Uint8Array,
  playerId: number,
  pflags: number,
  offset: number,
): { player: DeltaPlayerSnapshot; newOffset: number } {
  let fieldMask = 0;
  let posX: number | undefined;
  let posY: number | undefined;
  let posZ: number | undefined;
  let yaw: number | undefined;
  let pitch: number | undefined;
  let health: number | undefined;
  let armor: number | undefined;
  let weapon: string | undefined;
  let isCrouching: boolean | undefined;
  let skinId: number | undefined;
  let weaponSkinId: number | undefined;
  let money: number | undefined;
  let isGrounded: boolean | undefined;
  let isSliding: boolean | undefined;
  let grenades: Record<string, number> | undefined;
  let reloading: boolean | undefined;
  let inspecting: boolean | undefined;
  let isBombCarrier: boolean | undefined;
  let isOnLadder: boolean | undefined;
  let ladderId: number | undefined;
  let isMantling: boolean | undefined;
  let isInWater: boolean | undefined;
  let mantleDest: { x: number; y: number; z: number } | undefined;
  let mantleT: number | undefined;
  let team: number | undefined;
  let ownedWeapons: Record<string, boolean> | undefined;
  let removed: boolean | undefined;

  // Removed flag — no payload bytes, player was disconnected/removed
  if (pflags & ServerPlayerDeltaFlags.Removed) {
    removed = true;
    fieldMask |= DeltaFields.Removed;
    return {
      player: { id: playerId, fieldMask, removed },
      newOffset: offset,
    };
  }

  if (pflags & ServerPlayerDeltaFlags.Position) {
    posX = view.getFloat32(offset, true); offset += 4;
    posY = view.getFloat32(offset, true); offset += 4;
    posZ = view.getFloat32(offset, true); offset += 4;
    fieldMask |= DeltaFields.PosX | DeltaFields.PosY | DeltaFields.PosZ;
  }

  if (pflags & ServerPlayerDeltaFlags.Yaw) {
    yaw = view.getFloat32(offset, true); offset += 4;
    fieldMask |= DeltaFields.Yaw;
  }

  if (pflags & ServerPlayerDeltaFlags.Pitch) {
    pitch = view.getFloat32(offset, true); offset += 4;
    fieldMask |= DeltaFields.Pitch;
  }

  if (pflags & ServerPlayerDeltaFlags.Health) {
    health = bytes[offset++];
    fieldMask |= DeltaFields.Health;
  }

  if (pflags & ServerPlayerDeltaFlags.Armor) {
    armor = bytes[offset++];
    fieldMask |= DeltaFields.Armor;
  }

  if (pflags & ServerPlayerDeltaFlags.Weapon) {
    const wLen = bytes[offset++];
    if (offset + wLen > bytes.length) return { player: { id: playerId, fieldMask, posX, posY, posZ, yaw, pitch, health, armor, weapon, isCrouching }, newOffset: offset };
    weapon = new TextDecoder().decode(bytes.subarray(offset, offset + wLen));
    offset += wLen;
    // Server encodes weaponSkinId (uint16 LE) immediately after weapon string.
    weaponSkinId = bytes[offset] | (bytes[offset + 1] << 8);
    offset += 2;
    fieldMask |= DeltaFields.Weapon | DeltaFields.WeaponSkinId;
  }

  if (pflags & ServerPlayerDeltaFlags.Crouch) {
    isCrouching = bytes[offset++] !== 0;
    fieldMask |= DeltaFields.IsCrouching;
  }

  if (pflags & ServerPlayerDeltaFlags.Money) {
    // F-SN-010 (Protocol v9): server now emits Money as signed int32 LE
    // (4 bytes) instead of int16 LE (2 bytes) so transient bonus stacks
    // above 32767 round-trip without silent clamps. Atomic with the
    // server bump in delta.go:373-383 — never decode int16 against a v9
    // server (handshake at handleWelcome guards the version skew).
    money = view.getInt32(offset, true); offset += 4;
    // F-NC-007: tag the fieldMask so applyDelta can distinguish "explicitly
    // sent money=X" from "no update" instead of falling through to prev.money.
    fieldMask |= DeltaFields.Money;
  }

  if (pflags & ServerPlayerDeltaFlags.Grounded) {
    isGrounded = bytes[offset++] !== 0;
    fieldMask |= DeltaFields.IsGrounded;
  }

  if (pflags & ServerPlayerDeltaFlags.Sliding) {
    isSliding = bytes[offset++] !== 0;
    fieldMask |= DeltaFields.IsSliding;
  }

  if (pflags & ServerPlayerDeltaFlags.WeaponSkin) {
    weaponSkinId = view.getUint16(offset, true);
    offset += 2;
    fieldMask |= DeltaFields.WeaponSkinId;
  }

  if (pflags & ServerPlayerDeltaFlags.Grenades) {
    const grenadesLen = view.getUint16(offset, true);
    offset += 2;
    if (offset + grenadesLen > bytes.length) {
      return { player: { id: playerId, fieldMask, posX, posY, posZ, yaw, pitch, health, armor, weapon, isCrouching }, newOffset: offset };
    }
    const jsonStr = new TextDecoder().decode(bytes.subarray(offset, offset + grenadesLen));
    offset += grenadesLen;
    if (jsonStr.length === 0) {
      grenades = {};
    } else {
      try { grenades = JSON.parse(jsonStr); } catch { grenades = {}; }
    }
    // F-NC-008: tag fieldMask so applyDelta knows the empty/cleared payload was
    // an explicit reset rather than "field not present in this delta."
    fieldMask |= DeltaFields.Grenades;
  }

  // N-M-005: owned-weapons set — wire layout mirrors Grenades (uint16 LE length
  // prefix + JSON Record<string,boolean>). Decoded here so the buy-menu rebuy
  // gate sees ownership updates between 0xFE keyframes, not only on them.
  if (pflags & ServerPlayerDeltaFlags.OwnedWeapons) {
    const ownedLen = view.getUint16(offset, true);
    offset += 2;
    if (offset + ownedLen > bytes.length) {
      return { player: { id: playerId, fieldMask, posX, posY, posZ, yaw, pitch, health, armor, weapon, isCrouching }, newOffset: offset };
    }
    const jsonStr = new TextDecoder().decode(bytes.subarray(offset, offset + ownedLen));
    offset += ownedLen;
    if (jsonStr.length === 0) {
      ownedWeapons = {};
    } else {
      try { ownedWeapons = JSON.parse(jsonStr); } catch { ownedWeapons = {}; }
    }
    fieldMask |= DeltaFields.OwnedWeapons;
  }

  if (pflags & ServerPlayerDeltaFlags.Reloading) {
    reloading = bytes[offset++] !== 0;
    fieldMask |= DeltaFields.Reloading;
  }

  if (pflags & ServerPlayerDeltaFlags.Inspecting) {
    inspecting = bytes[offset++] !== 0;
    fieldMask |= DeltaFields.Inspecting;
  }

  if (pflags & ServerPlayerDeltaFlags.BombCarrier) {
    isBombCarrier = bytes[offset++] !== 0;
    fieldMask |= DeltaFields.BombCarrier;
  }

  if (pflags & ServerPlayerDeltaFlags.OnLadder) {
    isOnLadder = bytes[offset++] !== 0;
    fieldMask |= DeltaFields.IsOnLadder;
  }

  if (pflags & ServerPlayerDeltaFlags.LadderID) {
    ladderId = bytes[offset++];
    fieldMask |= DeltaFields.LadderId;
  }

  if (pflags & ServerPlayerDeltaFlags.Mantling) {
    isMantling = bytes[offset++] !== 0;
    fieldMask |= DeltaFields.IsMantling;
  }

  if (pflags & ServerPlayerDeltaFlags.InWater) {
    isInWater = bytes[offset++] !== 0;
    fieldMask |= DeltaFields.IsInWater;
  }

  if (pflags & ServerPlayerDeltaFlags.MantleDest) {
    mantleDest = {
      x: view.getFloat32(offset, true),
      y: view.getFloat32(offset + 4, true),
      z: view.getFloat32(offset + 8, true),
    };
    offset += 12;
    fieldMask |= DeltaFields.MantleDest;
  }

  if (pflags & ServerPlayerDeltaFlags.MantleT) {
    mantleT = bytes[offset++];
    fieldMask |= DeltaFields.MantleT;
  }

  // F-NC-005: player character SkinID — uint16 LE.
  if (pflags & ServerPlayerDeltaFlags.Skin) {
    skinId = view.getUint16(offset, true);
    offset += 2;
    fieldMask |= DeltaFields.SkinId;
  }

  // F-NC-006: team byte — uint8.
  if (pflags & ServerPlayerDeltaFlags.Team) {
    team = bytes[offset++];
    fieldMask |= DeltaFields.Team;
  }

  return {
    player: {
      id: playerId,
      fieldMask,
      posX, posY, posZ,
      yaw, pitch,
      health, armor,
      weapon,
      isCrouching,
      skinId,
      weaponSkinId,
      money,
      grenades,
      ownedWeapons,
      isGrounded,
      isSliding,
      reloading,
      inspecting,
      isBombCarrier,
      isOnLadder,
      ladderId,
      isMantling,
      isInWater,
      mantleDest,
      mantleT,
      team,
    },
    newOffset: offset,
  };
}

/**
 * Decode bomb state from binary delta.
 */
function decodeBinaryBomb(
  view: DataView,
  bytes: Uint8Array,
  offset: number,
): { bomb: BinaryBombState | null; newOffset: number } {
  if (offset + 1 > bytes.length) return { bomb: null, newOffset: offset };
  const flags = bytes[offset++];
  const hasBomb = (flags & (1 << 5)) !== 0;

  if (!hasBomb) {
    return { bomb: null, newOffset: offset };
  }

  // Need at least 2 (planterId+defuserId) + 12 (3 floats) + 1 (siteLen) = 15 bytes
  if (offset + 15 > bytes.length) return { bomb: null, newOffset: offset };
  const planterId = bytes[offset++];
  const defuserId = bytes[offset++];
  const timer = view.getFloat32(offset, true); offset += 4;
  const plantProgress = view.getFloat32(offset, true); offset += 4;
  const defuseProgress = view.getFloat32(offset, true); offset += 4;
  const siteLen = bytes[offset++];
  if (offset + siteLen > bytes.length) return { bomb: null, newOffset: offset };
  const plantSite = new TextDecoder().decode(bytes.subarray(offset, offset + siteLen));
  offset += siteLen;

  return {
    bomb: {
      isPlanted: (flags & (1 << 0)) !== 0,
      isDetonated: (flags & (1 << 1)) !== 0,
      isDefused: (flags & (1 << 2)) !== 0,
      isPlanting: (flags & (1 << 3)) !== 0,
      isDefusing: (flags & (1 << 4)) !== 0,
      planterId,
      defuserId,
      timer,
      plantProgress,
      defuseProgress,
      plantSite,
    },
    newOffset: offset,
  };
}

// ============================================================
// Encoding — JSON (legacy) + Binary
// ============================================================

/**
 * Encode input as FlatBuffer binary (preferred) or JSON string (fallback).
 * Returns ArrayBuffer for binary transport.
 *
 * N-L-005: Reserved for the binary-input upgrade (N-C-002 future work).
 * The hot input path goes through `connection.ts` directly using the
 * slot-keyed Uint8Array encoder; this wrapper is exercised by
 * `messages.test.ts` only and stays for parity with the decoder side.
 */
export function encodeInput(input: InputMessage): ArrayBuffer {
  return encodeInputBinary(input);
}

/** Encode input as JSON string (for WebSocket text transport). */
export function encodeInputJSON(input: InputMessage): string {
  return JSON.stringify({
    type: 'input',
    data: input,
  });
}

export function encodeJoin(name: string): string {
  return JSON.stringify({
    type: 'join',
    data: { name },
  });
}

/**
 * Encode a weapon-switch message.
 *
 * Server-side handler in `server/internal/net/websocket.go` expects
 * `data.weaponId` (string) — the canonical loadout-aware identifier
 * (`'glock'`, `'awp'`, `'knife'`, ...). Earlier drafts of this helper
 * shipped `{ slot }` (a 1-2-3 quickslot index) which the server silently
 * rejected. Always pass the weapon id string here.
 */
export function encodeWeaponSwitch(weaponId: string): string {
  return JSON.stringify({
    type: 'weapon_switch',
    data: { weaponId },
  });
}

export function encodeReload(): string {
  return JSON.stringify({
    type: 'reload',
    data: {},
  });
}

// ============================================================
// Decoding — handles both FlatBuffer and JSON
// ============================================================

/**
 * Decode a snapshot from either FlatBuffer binary or JSON string.
 * Automatically detects format.
 */
export function decodeSnapshot(data: ArrayBuffer | string): WorldSnapshot {
  if (data instanceof ArrayBuffer) {
    return decodeFlatBufferSnapshot(data) ?? { tick: 0, yourSequenceAck: 0, players: [], events: [] };
  }

  // JSON path (legacy fallback)
  return decodeSnapshotJSON(data);
}

/**
 * Decode a snapshot or delta snapshot from raw data.
 * If the message contains isDelta flag, returns DeltaWorldSnapshot.
 */
export function decodeSnapshotOrDelta(data: ArrayBuffer | string): WorldSnapshot | DeltaWorldSnapshot {
  if (data instanceof ArrayBuffer) {
    // N-L-002: This is the legacy FlatBuffer-as-full-snapshot fallback path
    // (used by the WebSocket text/binary transport in older builds). Binary
    // delta + keyframe encoding is handled by `decodeBinarySnapshot` /
    // `decodeBinaryDelta` above (0xFD/0xFE byte-prefixed payloads on the
    // WebRTC DataChannel); this branch only fires when a raw FlatBuffer
    // payload reaches the JSON-style decode entry point.
    return decodeFlatBufferSnapshot(data) ?? { tick: 0, yourSequenceAck: 0, players: [], events: [] };
  }

  const parsed = JSON.parse(data);

  if (parsed.type === 'delta_snapshot') {
    return decodeDeltaSnapshotJSON(parsed.data);
  }

  if (parsed.type !== 'snapshot') {
    throw new Error(`Expected snapshot message, got "${parsed.type}"`);
  }

  return validateWorldSnapshot(parsed.data as WorldSnapshot);
}

/** Decode a JSON-encoded full snapshot. */
function decodeSnapshotJSON(raw: string): WorldSnapshot {
  const parsed = JSON.parse(raw);

  if (parsed.type !== 'snapshot') {
    throw new Error(`Expected snapshot message, got "${parsed.type}"`);
  }

  return validateWorldSnapshot(parsed.data as WorldSnapshot);
}

/** Decode a JSON-encoded delta snapshot. */
function decodeDeltaSnapshotJSON(data: any): DeltaWorldSnapshot {
  return {
    tick: data.tick,
    yourSequenceAck: data.yourSequenceAck,
    players: (data.players || []).map((p: any) => ({
      id: p.id,
      fieldMask: p.fieldMask,
      posX: p.posX,
      posY: p.posY,
      posZ: p.posZ,
      yaw: p.yaw,
      pitch: p.pitch,
      health: p.health,
      armor: p.armor,
      weapon: p.weapon,
      isCrouching: p.isCrouching,
      skinId: p.skinId,
      weaponSkinId: p.weaponSkinId,
      money: p.money,
      team: p.team,
      grenades: p.grenades,
      isGrounded: p.isGrounded,
      isSliding: p.isSliding,
      reloading: p.reloading,
      inspecting: p.inspecting,
      isBombCarrier: p.isBombCarrier,
      isOnLadder: p.isOnLadder,
      ladderId: p.ladderId,
      isMantling: p.isMantling,
      isInWater: p.isInWater,
      mantleDest: p.mantleDest,
      mantleT: p.mantleT,
      ownedWeapons: p.ownedWeapons,
      removed: p.removed,
    })),
    events: data.events || [],
    isDelta: true,
  };
}

/** Validate a WorldSnapshot has required fields. */
function validateWorldSnapshot(snap: WorldSnapshot): WorldSnapshot {
  if (typeof snap.tick !== 'number') {
    throw new Error('Invalid snapshot: missing tick');
  }
  if (typeof snap.yourSequenceAck !== 'number') {
    throw new Error('Invalid snapshot: missing yourSequenceAck');
  }
  if (!Array.isArray(snap.players)) {
    throw new Error('Invalid snapshot: missing players array');
  }

  for (const p of snap.players) {
    if (typeof p.id !== 'number') {
      throw new Error('Invalid player snapshot: missing id');
    }
    if (
      p.position === null ||
      p.position === undefined ||
      typeof p.position.x !== 'number' ||
      typeof p.position.y !== 'number' ||
      typeof p.position.z !== 'number'
    ) {
      throw new Error(`Invalid player snapshot: bad position for player ${p.id}`);
    }
  }

  if (!Array.isArray(snap.events)) {
    snap.events = [];
  }

  return snap;
}
